import React from "react";

import { css, cx } from "@emotion/css";
import { Editor } from "@tiptap/core";
import { EditorContent } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";

import { useStyles } from "@/hooks/useStyles.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { CenteredLoader } from "@/uiKit/atoms/Loader.tsx";
import { StyleClasses } from "@/uiKit/atoms/utils/AtomTypes.ts";
import { RichTextBubbleMenu } from "@/uiKit/molecules/RichText/RichTextBubbleMenu.tsx";
import { RichTextEditionHeader } from "@/uiKit/molecules/RichText/RichTextEditionHeader.tsx";
import { useRichTextEditor } from "@/uiKit/molecules/RichText/RichTextSettings.ts";

export type TextEditorProps = {
  content: string | undefined;
  onChange: (content: string) => void;
  hideHeader?: boolean;
  placeholder?: string;
  /**
   * Prefix of the editor ids, for pages rendering several editors at once: the editor is `${idPrefix}-rich-text-editor`
   * and its header, bubble menu and content derive from it. Without it the editor is `rich-text-editor`.
   */
  idPrefix?: string;
  className?: string;
  classes?: StyleClasses<"root" | "content" | "header" | "bubbleMenu" | "editor">;
};

export const RichTextEditor: React.FC<TextEditorProps> = ({
  content,
  placeholder,
  hideHeader = false,
  onChange,
  idPrefix,
  className,
  classes,
}) => {
  const styles = useStyles(makeStyles);
  const editorId = idPrefix ? `${idPrefix}-rich-text-editor` : "rich-text-editor";
  const handleChange = (editor: { editor: Editor }) => {
    onChange(JSON.stringify(editor.editor.getJSON()));
  };
  const editor = useRichTextEditor({
    content: content ? JSON.parse(content) : undefined,
    onUpdate: handleChange,
    extensions: {
      placeholder,
    },
  });

  if (!editor) {
    return <CenteredLoader />;
  }

  return (
    <div className={cx(styles.container, className, classes?.root)} id={editorId}>
      {!hideHeader && (
        <RichTextEditionHeader
          id={`${editorId}-header`}
          editor={editor}
          className={cx(styles.header, classes?.header)}
        />
      )}
      <div className={cx(styles.content, classes?.content)}>
        <BubbleMenu
          editor={editor}
          className={cx(styles.bubbleMenu, classes?.bubbleMenu)}
          options={{
            placement: "bottom-start",
            offset: 8,
            flip: true,
            shift: true,
          }}
        >
          <RichTextBubbleMenu id={`${editorId}-bubble-menu`} editor={editor} />
        </BubbleMenu>
        <EditorContent
          id={`${editorId}-content`}
          editor={editor}
          className={cx(styles.editor, classes?.editor)}
          content={content}
        />
      </div>
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({}),
  header: css({
    position: "sticky",
    top: 0,
    zIndex: 1,
  }),
  content: css({
    zIndex: 0,
    borderColor: theme.palette.divider,
    borderWidth: 1,
    borderStyle: "solid",
    borderRadius: theme.spacing(4),
    padding: theme.spacing(2),
  }),
  editor: css({
    "& .ProseMirror": {
      outline: "none",
    },
  }),

  bubbleMenu: css({
    zIndex: theme.zIndex.tooltip,
  }),
});
